import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { BlockRegistryItem, BlockUsageDetail } from '@audit/shared-types';

const TYPE_BADGE: Record<'moj' | 'core' | 'other', string> = {
  moj: 'bg-purple-100 text-purple-700',
  core: 'bg-blue-100 text-blue-700',
  other: 'bg-gray-100 text-gray-600',
};

const TYPE_LABEL: Record<'moj' | 'core' | 'other', string> = {
  moj: 'MoJ',
  core: 'Core',
  other: 'Other',
};

function formatDateTime(value: string | null) {
  if (!value) {
    return 'Not recorded';
  }

  return new Date(value).toLocaleString();
}

export default function BlockDetailsPage() {
  const [searchParams] = useSearchParams();
  const blockName = searchParams.get('blockName') ?? '';
  const siteIdParam = searchParams.get('siteId');
  const siteId = siteIdParam ? Number(siteIdParam) : undefined;

  const { data: details, isLoading, isError } = useQuery({
    queryKey: ['blocks', 'usage', 'details', blockName, siteId],
    queryFn: () =>
      apiClient
        .get<BlockUsageDetail>('/blocks/usage/details', {
          params: {
            blockName,
            ...(siteId != null ? { siteId } : {}),
          },
        })
        .then((response) => response.data),
    enabled: blockName.length > 0,
  });

  const { data: registry = [] } = useQuery({
    queryKey: ['blocks', 'registry'],
    queryFn: () => apiClient.get<BlockRegistryItem[]>('/blocks/registry').then((response) => response.data),
    staleTime: 5 * 60 * 1000,
  });

  if (!blockName) {
    return (
      <div className="space-y-4">
        <Link to="/blocks" className="text-sm text-brand-600 hover:text-brand-700">
          ← Back to block usage
        </Link>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900">Block details</h2>
          <p className="mt-2 text-sm text-gray-500">Choose a block from the Block Usage table first.</p>
        </div>
      </div>
    );
  }

  const registryEntry = registry.find((item) => item.name === blockName);
  const blockType = (registryEntry?.blockType ??
    (blockName.startsWith('mojblocks/') ? 'moj' : blockName.startsWith('core/') ? 'core' : 'other')) as
    | 'moj'
    | 'core'
    | 'other';
  const pageHeading = registryEntry?.title ?? blockName.split('/').pop() ?? blockName;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/blocks" className="text-sm text-brand-600 hover:text-brand-700">
            ← Back to block usage
          </Link>
          <h2 className="mt-2 text-2xl font-bold text-gray-900">{pageHeading}</h2>
          <p className="mt-1 font-mono text-xs text-gray-400">{blockName}</p>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${TYPE_BADGE[blockType]}`}>
          {TYPE_LABEL[blockType]}
        </span>
      </div>

      {registryEntry && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Block information</p>
              <p className="mt-2 text-sm text-gray-600">{registryEntry.description || 'No registry description available.'}</p>
            </div>
            {registryEntry.deprecated && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
                Deprecated
              </span>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-500">
            <span className="rounded-full bg-gray-100 px-3 py-1">Category: {registryEntry.category || 'Uncategorised'}</span>
            {siteId != null && <span className="rounded-full bg-gray-100 px-3 py-1">Filtered to site {siteId}</span>}
          </div>
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-500">Loading block details…</p>}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load block details.
        </div>
      )}

      {details && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Recorded uses</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">{details.totalCount}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Sites</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">{details.siteCount}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Pages</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">{details.pageCount}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Last seen</p>
              <p className="mt-2 text-sm font-medium text-gray-900">{formatDateTime(details.lastSeen)}</p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-sm font-semibold text-gray-900">Sites using this block</h3>
              <p className="mt-1 text-xs text-gray-500">A simple summary of where this block is currently used across the platform.</p>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <th className="px-6 py-3">Site</th>
                  <th className="px-6 py-3">Uses</th>
                  <th className="px-6 py-3">Pages</th>
                </tr>
              </thead>
              <tbody>
                {details.sites.map((site) => (
                  <tr key={site.siteId} className="border-t border-gray-100 align-top">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{site.siteName}</div>
                      <a href={site.siteUrl} target="_blank" rel="noreferrer" className="mt-1 block text-xs text-brand-600 hover:text-brand-700">
                        {site.siteUrl}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{site.totalCount}</td>
                    <td className="px-6 py-4 text-gray-500">
                      <div>{site.pageCount}</div>
                      <div className="mt-1 text-xs text-gray-400">Last seen {formatDateTime(site.lastSeen)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {details.sites.length === 0 && (
              <div className="px-6 py-8 text-sm text-gray-500">No usage records found for this block.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}